<template>
  <div class="chats-view">
    <!-- Header, der den Chatpartner anzeigt -->
    <aside class="chat-note chat-header" role="note" aria-live="polite">
      <div class="chat-header-inner">
        <!-- Avatar des Chatpartners -->
        <img class="chat-avatar" :src="otherAvatarUrl" :alt="`Profilbild von ${otherDisplayName}`" @error="onAvatarError"/>

        <div class="chat-header-text">
          <!-- Name und E-Mail des Chatpartners -->
          <h2>Privater Chat mit: {{ otherDisplayName }}</h2>
          <p class="chat-subtitle">{{ otherDisplayEmail }}</p>
        </div>
      </div>
    </aside>

    <!-- Die eigentliche Chat-Komponente, die die eindeutige Chat-ID erhält -->
    <div class="chatDiv">
      <div v-if="!chatID" class="chat-loading">Lade Chat...</div>
      <ChatView v-else :chatIdProp="chatID" :general="true" />
    </div>

    <aside class="chat-note" role="note" aria-live="polite">
      <strong>Hinweis:</strong> Bitte verhalte dich freundlich und respektvoll im Chat. Wir speichern deine IP-Adresse!
    </aside>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import ChatView from './ChatView.vue'

import { DEFAULT_PROFILE_IMAGE } from '@/assets/config'
import { resolveApiUrl, getUserByEmail } from '@/services/api'

// Holt den Router und den globalen Auth-Status.
const route = useRoute()
const { user } = useAuth()

// Lokale Zustandsvariablen für die Chat-ID und die Daten des Chatpartners.
const chatID = ref('')
const otherUser = ref(null)

/**
 * Normalisiert eine E-Mail-Adresse für konsistente Vergleiche.
 * @param {string} email - Die zu normalisierende E-Mail.
 */
const normalizeEmail = (email) => String(email ?? '').trim().toLowerCase()

/**
 * Konvertiert einen ArrayBuffer (von der Hash-Funktion) in einen Hex-String.
 * @param {ArrayBuffer} buffer - Der zu konvertierende Buffer.
 */
const toHex = (buffer) => {
  return Array.from(new Uint8Array(buffer)).map((b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Erstellt eine eindeutige und konsistente Chat-ID für einen privaten Chat zwischen zwei Benutzern.
 * @param {string} emailA - E-Mail des ersten Benutzers.
 * @param {string} emailB - E-Mail des zweiten Benutzers.
 * @returns {Promise<string>} Die generierte Chat-ID.
 */
const buildPrivateChatId = async (emailA, emailB) => {
  const a = normalizeEmail(emailA)
  const b = normalizeEmail(emailB)

  if (!a || !b) return ''

  // Sortiert die E-Mails alphabetisch, um sicherzustellen, dass die ID immer gleich ist,
  // unabhängig davon, wer den Chat initiiert (A->B ist dieselbe ID wie B->A).
  const [first, second] = [a, b].sort((x, y) => x.localeCompare(y, 'de', { sensitivity: 'base' }))
  const canonical = `${first}|${second}`

  // Verwendet die moderne Crypto-API, um einen sicheren SHA-256-Hash zu erstellen.
  // Dies verhindert, dass E-Mail-Adressen aus der Chat-ID abgeleitet werden können.
  if (window.crypto?.subtle) {
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonical));
    return `dm${toHex(hashBuffer)}`
  }

  //für uralte browser manuelles hashen:
  let h = 2166136261
  for (let i = 0; i < canonical.length; i++) {
    h ^= canonical.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return `dm${(h >>> 0).toString(36)}`
}

// Die E-Mail des eingeloggten Benutzers.
const ownEmail = computed(() => normalizeEmail(user.value?.email))

// Die E-Mail des Chatpartners aus der URL.
const routeEmail = computed(() => {
  const raw = route.params.email
  if (!raw) return ''
  try {
    return normalizeEmail(decodeURIComponent(String(raw)))
  } catch {
    return normalizeEmail(String(raw))
  }
})

// Wenn kein Parameter in der URL ist, wird ein "Chat mit sich selbst" angenommen.
const targetEmail = computed(() => routeEmail.value || ownEmail.value)

// Berechnete Eigenschaften für die Anzeige des Namens, der E-Mail und des Avatars des Chatpartners.
const otherDisplayName = computed(() => {
  const u = otherUser.value
  return (
    u?.nickname ||
    u?.username ||
    u?.name ||
    u?.email ||
    targetEmail.value ||
    'Unbekannt'
  )
})

const otherDisplayEmail = computed(() => {
  return otherUser.value?.email || targetEmail.value || ''
})

const otherAvatarUrl = computed(() => {
  const raw = otherUser.value?.profileimage || DEFAULT_PROFILE_IMAGE
  return resolveApiUrl(raw)
})

/**
 * Fallback-Handler, falls das Avatar-Bild nicht geladen werden kann.
 */
const onAvatarError = (e) => {
  const img = e.target
  if (img && img.src !== DEFAULT_PROFILE_IMAGE) {
    img.src = DEFAULT_PROFILE_IMAGE
  }
}

// Ein Token, um Race Conditions zu verhindern, wenn der Benutzer schnell zwischen Chats navigiert.
let requestToken = 0

// Beobachtet Änderungen am eigenen oder am Ziel-Benutzer.
watch(
  [ownEmail, targetEmail],
  async ([me, other]) => {
    // Erhöht das Token bei jedem neuen Aufruf.
    const currentToken = ++requestToken

    if (!me || !other) {
      chatID.value = ''
      otherUser.value = null
      return
    }

    // Generiert die Chat-ID.
    chatID.value = await buildPrivateChatId(me, other)

    // Sonderfall: Chat mit sich selbst.
    if (other === me) {
      otherUser.value = user.value ?? null
      return
    }

    try {
      // Ruft die Benutzerdaten des Chatpartners von der API ab.
      const res = await getUserByEmail(other)
      // Bricht ab, wenn in der Zwischenzeit eine neuere Anfrage gestartet wurde.
      if (currentToken !== requestToken) return
      otherUser.value = res?.answer ?? res ?? null
    } catch {
      // Bricht auch bei einem Fehler ab, wenn eine neuere Anfrage läuft.
      if (currentToken !== requestToken) return
      // Fallback, wenn der Benutzer nicht gefunden wurde.
      otherUser.value = { email: other }
    }
  },
  { immediate: true }
)

onMounted(() => {
  if (user.value?.email && targetEmail.value) {
    // watch erledigt den Rest
  }
})
</script>

<style scoped lang="scss">
.chats-view {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 60px);
  overflow: hidden;
  min-height: 0;
}

.chats-view h2 {
  padding: 0;
  margin: 0;
}

.chatDiv {
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-grow: 1;
  min-height: 0;
  overflow: hidden;
}

.chat-note {
  background: rgba(255,255,255,0.03);
  color: inherit;
  padding: 0.65rem 1rem;
  margin: 0.5rem;
  border-radius: 10px;
  font-size: 0.9rem;
  border: 1px solid rgba(255,255,255,0.04);
  align-self: stretch;
}

.chat-header {
  padding: 0.85rem 1rem;
}

.chat-header-inner {
  display: flex;
  align-items: center;
  gap: 0.9rem;
}

.chat-avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid rgba(255,255,255,0.12);
  background: rgba(255,255,255,0.06);
}

.chat-header-text {
  min-width: 0;
}

.chat-subtitle {
  margin: 0.2rem 0 0;
  opacity: 0.75;
  font-size: 0.85rem;
  word-break: break-word;
}

.chat-loading {
  padding: 1rem;
  opacity: 0.8;
}
</style>